import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import {
  ReactCompareSlider,
  ReactCompareSliderImage
} from "react-compare-slider";

class ComparisonErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('ReactCompareImage Error in proposal view:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface BeforeAfterProject {
  id: string;
  before_image_url: string;
  after_image_url: string;
  title: string;
  description: string | null;
}

export function ProposalBeforeAfterGallery() {
  const [projects, setProjects] = useState<BeforeAfterProject[]>([]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    const { data } = await supabase
      .from('proposal_before_after')
      .select('*')
      .eq('is_active', true)
      .order('display_order')
      .limit(2);

    setProjects(data || []);
  };

  if (projects.length === 0) return null;

  return (
    <section className="my-12 page-break-before">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Our Transformation Projects</h2>
        <p className="text-muted-foreground">
          See the dramatic difference our work makes
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {projects.map((project) => (
          <Card key={project.id} className="overflow-hidden page-break-inside-avoid">
            <CardContent className="p-0">
              <div className="aspect-video relative">
                {/* Interactive slider for screen viewing */}
                <div className="no-print">
                  <ComparisonErrorBoundary
                    fallback={
                      <div className="grid grid-cols-2 gap-2 h-full">
                        <div className="relative">
                          <img 
                            src={project.before_image_url} 
                            alt="Before" 
                            className="w-full h-full object-cover" 
                          />
                          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 text-xs rounded">
                            Before
                          </div>
                        </div>
                        <div className="relative">
                          <img 
                            src={project.after_image_url} 
                            alt="After" 
                            className="w-full h-full object-cover" 
                          />
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 text-xs rounded">
                            After
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <ReactCompareSlider
                      itemOne={
                        <ReactCompareSliderImage
                          src={project.before_image_url}
                          alt="Before"
                        />
                      }
                      itemTwo={
                        <ReactCompareSliderImage
                          src={project.after_image_url}
                          alt="After"
                        />
                      }
                      position={50}
                      style={{ width: '100%', height: '100%' }}
                      className="cursor-ew-resize"
                    />
                  </ComparisonErrorBoundary>
                </div>
                
                {/* Static side-by-side for printing */}
                <div className="print-only before-after-print-wrapper hidden">
                  <div>
                    <img src={project.before_image_url} alt="Before" className="w-full h-auto" />
                    <div className="before-after-print-label">Before</div>
                  </div>
                  <div>
                    <img src={project.after_image_url} alt="After" className="w-full h-auto" />
                    <div className="before-after-print-label">After</div>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-2">{project.title}</h3>
                {project.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {project.description}
                  </p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground no-print">
                  <span className="px-2 py-1 bg-muted rounded">Before</span>
                  <span>←</span>
                  <span>Slide to compare</span>
                  <span>→</span>
                  <span className="px-2 py-1 bg-primary/10 rounded">After</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
